<template>
  <article class="invoice-create-card invoice-create-card--accent-billing invoice-detail-operation-card">
    <header class="invoice-create-card__head">
      <h3>
        <i class="fa fa-briefcase invoice-create-section-icon invoice-create-section-icon--billing" aria-hidden="true"></i>
        {{ text.detail.cards.operation.title }}
      </h3>
    </header>

    <div class="invoice-detail-operation-split">
      <section class="invoice-detail-operation-pane">
        <dl class="invoice-detail-operation-list">
          <div class="invoice-detail-operation-row">
            <dt>{{ text.detail.cards.operation.fields.status }}</dt>
            <dd>
              <span class="app-status-pill expenses-status-pill" :class="resolvedStatusClass">
                {{ resolvedStatusLabel }}
              </span>
            </dd>
          </div>

          <div class="invoice-detail-operation-row invoice-detail-operation-row--customer">
            <dt>{{ customerLabel }}</dt>
            <dd>
              <router-link
                v-if="customerRoute"
                :to="customerRoute"
                class="invoice-detail-operation-link invoice-detail-operation-link--customer"
                :title="text.table.links.customerDetail"
              >
                {{ resolvedCustomerName }}
              </router-link>
              <span v-else>{{ resolvedCustomerName }}</span>
            </dd>
          </div>

          <div class="invoice-detail-operation-row invoice-detail-operation-row--sale">
            <dt>{{ text.detail.cards.operation.fields.sale }}</dt>
            <dd>
              <router-link
                v-if="saleRoute"
                :to="saleRoute"
                class="invoice-detail-operation-link invoice-detail-operation-link--sale"
                :title="text.table.links.saleDetail"
              >
                {{ resolvedSaleName }}
              </router-link>
              <span v-else>{{ resolvedSaleName }}</span>
            </dd>
          </div>

          <div class="invoice-detail-operation-spacer" aria-hidden="true"></div>

          <div class="invoice-detail-operation-row">
            <dt>{{ balanceLabel }}</dt>
            <dd>{{ resolvedBalance }}</dd>
          </div>

          <div v-if="showDueDate" class="invoice-detail-operation-row">
            <dt>{{ text.detail.cards.operation.fields.dueDate }}</dt>
            <dd>{{ resolvedDueDate }}</dd>
          </div>

          <div class="invoice-detail-operation-spacer" aria-hidden="true"></div>

          <div class="invoice-detail-operation-row">
            <dt>{{ text.detail.cards.operation.fields.sent }}</dt>
            <dd>{{ resolvedSent }}</dd>
          </div>

          <div class="invoice-detail-operation-row">
            <dt>{{ text.detail.cards.operation.fields.lastSent }}</dt>
            <dd>{{ resolvedLastSent }}</dd>
          </div>
        </dl>
      </section>

      <section class="invoice-detail-operation-pane invoice-detail-operation-pane--totals">
        <dl class="invoice-detail-operation-meta-list">
          <div class="invoice-detail-operation-meta-row">
            <dt>{{ text.detail.cards.operation.fields.relatedPayments }}</dt>
            <dd>
              <router-link
                v-if="paymentReceiptRoute"
                :to="paymentReceiptRoute"
                class="invoice-detail-operation-link"
              >
                {{ resolvedPaymentDisplay }}
              </router-link>
              <span v-else>{{ resolvedPaymentDisplay }}</span>
            </dd>
          </div>

          <div class="invoice-detail-operation-meta-divider" aria-hidden="true"></div>

          <div class="invoice-detail-operation-meta-row invoice-detail-operation-meta-row--comments">
            <dt>{{ commentsLabel }}</dt>
            <dd>
              <p
                class="invoice-detail-operation-comment-text"
                :class="{ 'invoice-detail-operation-comment-text--expanded': commentExpanded }"
              >
                {{ resolvedCommentsDisplay }}
              </p>

              <button
                v-if="showCommentToggle"
                type="button"
                class="invoice-detail-operation-comment-toggle"
                @click="commentExpanded = !commentExpanded"
              >
                {{ commentExpanded ? commentToggleLessLabel : commentToggleMoreLabel }}
              </button>
            </dd>
          </div>

          <div class="invoice-detail-operation-meta-divider" aria-hidden="true"></div>
        </dl>

        <h4 class="invoice-detail-operation-totals-title">{{ totalsTitle }}</h4>

        <dl class="invoice-detail-operation-totals-list">
          <div class="invoice-detail-operation-totals-row">
            <dt>{{ subtotalLabel }}</dt>
            <dd>{{ resolvedSubtotalAmount }}</dd>
          </div>

          <div v-if="showTaxes" class="invoice-detail-operation-totals-row">
            <dt>{{ taxesLabel }}</dt>
            <dd>{{ resolvedTaxesAmount }}</dd>
          </div>

          <div class="invoice-detail-operation-totals-divider" aria-hidden="true"></div>

          <div class="invoice-detail-operation-totals-row invoice-detail-operation-totals-row--total">
            <dt>{{ totalLabel }}</dt>
            <dd>{{ resolvedTotalAmount }}</dd>
          </div>
        </dl>
      </section>
    </div>
  </article>
</template>

<script>
import { formatDateShort, formatDateTime } from '@/utils/datePresentation';
import { formatAmountWithCurrency } from '@/utils/numberFormat';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: invoiceText } = useDomainText('invoices');

export default {
  name: 'InvoiceDetailOperationCard',
  props: {
    operation: {
      type: Object,
      default: () => ({}),
    },
    comments: {
      type: String,
      default: '',
    },
    subtotal: {
      type: Number,
      default: 0,
    },
    transferredTaxAmount: {
      type: Number,
      default: 0,
    },
    withheldTaxAmount: {
      type: Number,
      default: 0,
    },
    total: {
      type: Number,
      default: 0,
    },
    currency: {
      type: String,
      default: 'MXN',
    },
    showDueDate: {
      type: Boolean,
      default: true,
    },
    showTaxes: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      text: invoiceText,
      commentExpanded: false,
    };
  },
  computed: {
    fallbackValue() {
      return String(this.text?.detail?.page?.dateFallback || '-').trim() || '-';
    },
    resolvedStatusLabel() {
      const pendingLabel = String(this.text?.table?.statusPills?.pending || '').trim();
      return String(this.operation?.statusLabel || '').trim() || pendingLabel || this.fallbackValue;
    },
    resolvedStatusClass() {
      const key = String(this.operation?.statusKey || '').trim().toLowerCase();

      return {
        'invoices-status-pill--paid': key === 'done' || key === 'paid',
        'invoices-status-pill--partial': key === 'partial',
        'invoices-status-pill--pending': key === 'pending',
        'invoices-status-pill--canceled': key === 'canceled',
      };
    },
    resolvedPaymentDisplay() {
      const code = String(this.operation?.paymentReceiptCode || '').trim();
      const codesText = String(this.operation?.paymentReceiptCodesText || '').trim();
      if (codesText !== '') {
        return codesText;
      }

      if (code !== '') {
        return code;
      }

      const relatedLabel = String(this.operation?.relatedPaymentLabel || '').trim();
      if (relatedLabel !== '' && !['pagar', 'pago'].includes(relatedLabel.toLowerCase())) {
        return relatedLabel;
      }

      return '';
    },
    resolvedBalance() {
      const value = Number(this.operation?.balance);
      if (Number.isFinite(value)) {
        return formatAmountWithCurrency(value, this.operation?.currency || 'MXN');
      }

      return this.fallbackValue;
    },
    resolvedDueDate() {
      return formatDateShort(this.operation?.dueDate, { fallback: this.fallbackValue });
    },
    resolvedCustomerName() {
      const customerName = String(this.operation?.customerName || '').trim();
      if (customerName !== '') {
        return customerName;
      }

      return String(this.text?.detail?.cards?.operation?.values?.noCustomer || '').trim() || this.fallbackValue;
    },
    customerLabel() {
      return String(this.text?.detail?.cards?.operation?.fields?.customerShort || this.text?.detail?.cards?.operation?.fields?.customer || '').trim() || this.fallbackValue;
    },
    balanceLabel() {
      return String(this.text?.detail?.cards?.operation?.fields?.pendingBalance || this.text?.detail?.cards?.operation?.fields?.balance || '').trim() || this.fallbackValue;
    },
    customerRoute() {
      const customerId = Number(this.operation?.customerId || 0) || 0;
      if (customerId <= 0) {
        return null;
      }

      return {
        name: 'customerDetail',
        params: { customerId: String(customerId) },
      };
    },
    resolvedSaleName() {
      const saleCode = String(this.operation?.saleCode || '').trim();
      const saleName = String(this.operation?.saleName || '').trim();

      if (saleCode !== '' && saleName !== '') {
        return `${saleCode} - ${saleName}`;
      }

      if (saleCode !== '') {
        return saleCode;
      }

      if (saleName !== '') {
        return saleName;
      }

      return String(this.text?.detail?.cards?.operation?.values?.noSale || '').trim() || this.fallbackValue;
    },
    saleRoute() {
      const saleId = Number(this.operation?.saleId || 0) || 0;
      if (saleId <= 0) {
        return null;
      }

      return {
        name: 'saleDetail',
        params: { saleId: String(saleId) },
      };
    },
    paymentReceiptRoute() {
      const paymentReceiptId = Number(this.operation?.paymentReceiptId || 0) || 0;
      if (paymentReceiptId <= 0) {
        return null;
      }

      return {
        name: 'paymentReceiptDetail',
        params: { paymentReceiptId: String(paymentReceiptId) },
      };
    },
    resolvedSent() {
      return this.operation?.sent === true
        ? String(this.text?.detail?.cards?.operation?.values?.sent || '').trim() || this.fallbackValue
        : String(this.text?.detail?.cards?.operation?.values?.notSent || '').trim() || this.fallbackValue;
    },
    resolvedLastSent() {
      return formatDateTime(
        this.operation?.lastSendAt,
        { fallback: String(this.text?.detail?.cards?.operation?.values?.noLastSent || '').trim() || this.fallbackValue }
      );
    },
    totalsTitle() {
      return String(this.text?.detail?.cards?.totals?.title || '').trim() || this.fallbackValue;
    },
    subtotalLabel() {
      return String(this.text?.detail?.cards?.totals?.fields?.subtotal || '').trim() || this.fallbackValue;
    },
    taxesLabel() {
      return String(this.text?.detail?.cards?.totals?.fields?.taxes || '').trim() || this.fallbackValue;
    },
    totalLabel() {
      return String(this.text?.detail?.cards?.totals?.fields?.total || '').trim() || this.fallbackValue;
    },
    resolvedSubtotalAmount() {
      return formatAmountWithCurrency(this.subtotal, this.currency);
    },
    resolvedTaxesAmount() {
      const transferred = Number(this.transferredTaxAmount || 0);
      const withheld = Number(this.withheldTaxAmount || 0);
      return formatAmountWithCurrency(transferred - withheld, this.currency);
    },
    resolvedTotalAmount() {
      return formatAmountWithCurrency(this.total, this.currency);
    },
    commentsLabel() {
      return String(this.text?.detail?.cards?.operation?.fields?.comments || '').trim() || this.fallbackValue;
    },
    resolvedCommentsDisplay() {
      const normalized = String(this.comments || '').trim();
      return normalized;
    },
    showCommentToggle() {
      const normalized = String(this.comments || '').trim();
      return normalized.length > 90;
    },
    commentToggleMoreLabel() {
      return String(this.text?.detail?.cards?.operation?.actions?.showMore || '').trim() || this.fallbackValue;
    },
    commentToggleLessLabel() {
      return String(this.text?.detail?.cards?.operation?.actions?.showLess || '').trim() || this.fallbackValue;
    },
  },
};
</script>

<style scoped>
.invoice-detail-operation-card {
  height: 100%;
  padding: 14px 16px;
}

.invoice-detail-operation-card .invoice-create-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.invoice-detail-operation-split {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.95fr);
  gap: 16px;
  align-items: start;
}

.invoice-detail-operation-pane--totals {
  border-left: 1px solid rgba(148, 163, 184, 0.35);
  padding-left: 16px;
}

.invoice-detail-operation-meta-list {
  margin: 0;
  padding: 0;
}

.invoice-detail-operation-meta-row {
  display: grid;
  grid-template-columns: minmax(102px, auto) minmax(0, 1fr);
  column-gap: 10px;
  align-items: start;
}

.invoice-detail-operation-meta-row dt {
  margin: 0;
  font-size: 11px;
  line-height: 1.35;
  font-weight: 600;
  color: #7b879a;
}

.invoice-detail-operation-meta-row dd {
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 600;
  color: #334155;
}

.invoice-detail-operation-meta-divider {
  height: 1px;
  background: rgba(100, 116, 139, 0.34);
  margin: 9px 0;
}

.invoice-detail-operation-comment-text {
  margin: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.invoice-detail-operation-comment-text--expanded {
  white-space: normal;
}

.invoice-detail-operation-comment-toggle {
  border: 0;
  background: transparent;
  margin: 4px 0 0;
  padding: 0;
  color: #0070cd;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.invoice-detail-operation-comment-toggle:hover {
  color: #005a9f;
}

.invoice-detail-operation-link {
  color: #0070cd;
  text-decoration: none;
}

.invoice-detail-operation-link:hover {
  color: #0070cd;
  text-decoration: none;
}

.invoice-detail-operation-link--customer {
  font-weight: 700;
  text-transform: none;
}

.invoice-detail-operation-row--customer dd,
.invoice-detail-operation-row--customer dd * {
  text-transform: none !important;
}

.invoice-detail-operation-link--sale {
  font-weight: 400;
}

.invoice-detail-operation-action-link {
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  color: #0070cd;
  font-size: inherit;
  font-weight: 700;
  line-height: inherit;
  cursor: pointer;
  text-decoration: none;
}

.invoice-detail-operation-action-link:hover {
  color: #005a9f;
  text-decoration: none;
}

.invoice-detail-operation-header-action {
  border: 0;
  background: transparent;
  color: #0070cd;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  padding: 0;
}

.invoice-detail-operation-header-action:hover {
  color: #005a9f;
}

.invoice-detail-operation-list {
  margin: 0;
  padding: 0;
}

.invoice-detail-operation-row {
  display: grid;
  grid-template-columns: minmax(118px, auto) minmax(0, 1fr);
  align-items: baseline;
  column-gap: 12px;
  margin-bottom: 8px;
}

.invoice-detail-operation-row:last-child {
  margin-bottom: 0;
}

.invoice-detail-operation-spacer {
  height: 1px;
  background: rgba(148, 163, 184, 0.28);
  margin: 7px 0 12px;
}

.invoice-detail-operation-row dt {
  margin: 0;
  font-size: 11px;
  line-height: 1.35;
  font-weight: 600;
  color: #7b879a;
}

.invoice-detail-operation-row dd {
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 600;
  color: #334155;
  word-break: break-word;
}

.invoice-detail-operation-row--customer dd,
.invoice-detail-operation-row--sale dd {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.invoice-detail-operation-row--customer dd .invoice-detail-operation-link,
.invoice-detail-operation-row--customer dd > span,
.invoice-detail-operation-row--sale dd .invoice-detail-operation-link,
.invoice-detail-operation-row--sale dd > span {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.invoice-detail-operation-row .app-status-pill {
  vertical-align: middle;
  min-height: 26px;
  font-size: 10px;
}

.invoice-detail-operation-totals-title {
  margin: 2px 0 10px;
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.invoice-detail-operation-totals-list {
  margin: 0;
  padding: 0;
}

.invoice-detail-operation-totals-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 10px;
  align-items: baseline;
  margin-bottom: 10px;
}

.invoice-detail-operation-totals-row dt {
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 600;
  color: #64748b;
}

.invoice-detail-operation-totals-row dd {
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 700;
  color: #0f172a;
}

.invoice-detail-operation-totals-divider {
  height: 1px;
  background: rgba(100, 116, 139, 0.4);
  margin: 6px 0 12px;
}

.invoice-detail-operation-totals-row--total {
  margin-bottom: 0;
}

.invoice-detail-operation-totals-row--total dt,
.invoice-detail-operation-totals-row--total dd {
  font-weight: 800;
  letter-spacing: 0.01em;
}

.invoice-detail-operation-totals-row--total dt {
  font-size: 14px;
}

.invoice-detail-operation-totals-row--total dd {
  font-size: 13px;
}

@media (max-width: 767px) {
  .invoice-detail-operation-split {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .invoice-detail-operation-pane--totals {
    border-left: 0;
    border-top: 1px solid rgba(148, 163, 184, 0.35);
    padding-left: 0;
    padding-top: 12px;
  }

  .invoice-detail-operation-row {
    grid-template-columns: minmax(102px, auto) minmax(0, 1fr);
    column-gap: 10px;
    margin-bottom: 7px;
  }

  .invoice-detail-operation-row dt {
    font-size: 11px;
  }

  .invoice-detail-operation-row dd {
    font-size: 12px;
  }
}

.invoices-status-pill--paid {
  color: #1c9b43;
  background: rgba(59, 212, 46, 0.14);
  border: 1px solid rgba(59, 212, 46, 0.3);
}

.invoices-status-pill--partial {
  color: #1d4ed8;
  background: rgba(37, 99, 235, 0.12);
  border: 1px solid rgba(37, 99, 235, 0.28);
}

.invoices-status-pill--pending {
  color: #b45309;
  background: #fff7e8;
  border: 1px solid rgba(251, 191, 36, 0.55);
}

.invoices-status-pill--canceled {
  color: #b42318;
  background: #fff0f0;
  border: 1px solid rgba(197, 58, 58, 0.3);
}
</style>
